"use client";

import { IconCryptoWallet } from "@central-icons-react/square-outlined-radius-0-stroke-1.5";
import { IconAlertInfoOutline18, IconArrowUpRightOutline18, IconCheckOutline18, IconChevronDownOutline18, IconChevronRightOutline18, IconCodeOutline18, IconCopyOutline18, IconEarthOutline18, IconSparkle3Outline18 } from "nucleo-ui-outline-18";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { PixelLoader } from "@/components/ui/pixel-loader";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useOrganizations } from "@/hooks/useOrganizations";
import { useOrganizationWallets } from "@/hooks/useOrganizationWallets";
import { useSimBalance, useSimTokenInfo } from "@/hooks/useSimTokenInfo";
import type { X402Endpoint, X402PaymentDetails } from "@/hooks/useX402Endpoints";
import { formatX402Price, useAtomicImportX402Endpoint, useCheckX402Endpoint, useProbeX402Endpoint } from "@/hooks/useX402Endpoints";
import Link from "next/link";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ParamList } from "./X402ImportWizardSteps";
import { buildExampleInputFromParams, networkToChainId, type X402ImportWizardProps } from "./X402ImportWizardUtils";

// Re-export types and components for backward compatibility
export type { X402ImportStep, X402ImportWizardProps } from "./X402ImportWizardUtils";
export { X402StepIndicator } from "./X402ImportWizardSteps";
export function useX402ImportWizard({
  initialUrl = "",
  onImported,
  onCancel,
  onImportAnother,
  isActive = true,
  variant = "page"
}: X402ImportWizardProps) {
  const [step, setStep] = useState<"input" | "probe" | "importing" | "success">("input");
  const [url, setUrl] = useState(initialUrl);
  const [requestBody, setRequestBody] = useState("");
  const [requestMethod, setRequestMethod] = useState("");
  const [showDiscoveredParams, setShowDiscoveredParams] = useState(false);
  const [showResponseData, setShowResponseData] = useState(false);
  const [selectedWalletAddress, setSelectedWalletAddress] = useState<string>("");
  const [queryParamValues, setQueryParamValues] = useState<Record<string, string>>({});

  // Success state
  const [importedEndpoint, setImportedEndpoint] = useState<X402Endpoint | null>(null);
  const [paymentDetails, setPaymentDetails] = useState<X402PaymentDetails | null>(null);

  // Client-side validation errors (e.g., invalid JSON)
  const [clientError, setClientError] = useState<string | null>(null);

  // Hooks
  const {
    exists,
    endpoint: existingEndpoint,
    isLoading: isChecking
  } = useCheckX402Endpoint(url || null);
  const {
    probe,
    isProbing,
    probeResult,
    probeError,
    reset: resetProbe
  } = useProbeX402Endpoint();
  const {
    atomicImport,
    isImporting,
    importError,
    reset: resetImport
  } = useAtomicImportX402Endpoint();
  const {
    selectedOrgId
  } = useOrganizations();
  const {
    wallets: orgWallets,
    isLoading: isLoadingWallets
  } = useOrganizationWallets(isActive ? selectedOrgId ?? undefined : undefined);

  // Fetch token info (symbol) when probe result doesn't have it
  const probeChainId = networkToChainId(probeResult?.accepts?.[0]?.network);
  const probeTokenAddress = probeResult?.accepts?.[0]?.asset;
  const needsTokenLookup = !probeResult?.tokenSymbol && !!probeTokenAddress && !!probeChainId;
  const {
    data: tokenInfoData
  } = useSimTokenInfo(probeTokenAddress, probeChainId, needsTokenLookup);

  // Resolve token symbol: prefer probe result, then fetched data, then fallback
  const resolvedTokenSymbol = probeResult?.tokenSymbol || tokenInfoData?.tokens?.[0]?.symbol || probeResult?.tokenName || "tokens";

  // Fetch wallet balance for selected wallet when probe result is available
  const shouldFetchBalance = !!selectedWalletAddress && !!probeChainId && step === "probe";
  const {
    data: walletBalanceData,
    isLoading: isLoadingBalance
  } = useSimBalance(selectedWalletAddress || undefined, probeChainId ? [probeChainId] : undefined, shouldFetchBalance);

  // Check if wallet has sufficient balance for the payment
  const balanceCheckResult = useMemo(() => {
    if (!probeResult?.accepts?.[0] || !walletBalanceData?.balances) {
      return {
        balance: null,
        isSufficient: true,
        formattedBalance: null
      };
    }
    const requiredAmount = probeResult.accepts[0].maxAmountRequired;
    const tokenAddress = probeResult.accepts[0].asset;
    const tokenDecimals = probeResult.tokenDecimals || 6;

    // Find the matching token balance
    const tokenBalance = walletBalanceData.balances.find(b => b.chain_id === probeChainId && b.address.toLowerCase() === tokenAddress?.toLowerCase());
    if (!tokenBalance) {
      return {
        balance: "0",
        isSufficient: false,
        formattedBalance: "0"
      };
    }
    const balanceAmount = BigInt(tokenBalance.amount);
    const requiredAmountBigInt = BigInt(requiredAmount);
    const isSufficient = balanceAmount >= requiredAmountBigInt;

    // Format balance for display
    const formattedBalance = formatX402Price(tokenBalance.amount, tokenDecimals);
    return {
      balance: tokenBalance.amount,
      isSufficient,
      formattedBalance
    };
  }, [probeResult, walletBalanceData, probeChainId]);

  // Reset state when isActive changes to false (for modal usage)
  useEffect(() => {
    if (!isActive) {
      setStep("input");
      setUrl(initialUrl);
      setRequestBody("");
      setRequestMethod("");
      setShowDiscoveredParams(false);
      setShowResponseData(false);
      setSelectedWalletAddress("");
      setQueryParamValues({});
      setImportedEndpoint(null);
      setPaymentDetails(null);
      setClientError(null);
      resetProbe();
      resetImport();
    }
  }, [isActive, initialUrl, resetProbe, resetImport]);

  // Auto-select default org wallet (or first wallet) when loaded
  useEffect(() => {
    if (orgWallets.length > 0 && !selectedWalletAddress) {
      const defaultWallet = orgWallets.find(w => w.isDefault);
      setSelectedWalletAddress(defaultWallet?.address ?? orgWallets[0].address);
    }
  }, [orgWallets, selectedWalletAddress]);
  const handleProbe = useCallback(async () => {
    if (!url) return;
    try {
      // Pass optional body and method for endpoints that require specific input/method
      const result = await probe({
        resourceUrl: url,
        body: requestBody || undefined,
        method: requestMethod || undefined
      });
      setStep("probe");
      // Pre-fill request body from discovered params if not already set
      if (!requestBody && result?.bodyParams) {
        const exampleInput = buildExampleInputFromParams(result.bodyParams);
        if (exampleInput) {
          setRequestBody(exampleInput);
        }
      }
      // Initialize query param values from discovered params
      if (result?.queryParams) {
        const initialQueryValues: Record<string, string> = {};
        for (const [name, param] of Object.entries(result.queryParams)) {
          if (param.default !== undefined) {
            initialQueryValues[name] = String(param.default);
          } else if (param.enum && param.enum.length > 0) {
            initialQueryValues[name] = String(param.enum[0]);
          } else {
            initialQueryValues[name] = "";
          }
        }
        setQueryParamValues(initialQueryValues);
      }
    } catch {
      // Error handled by hook
    }
  }, [url, requestBody, requestMethod, probe]);

  // Build URL with query parameters
  const buildUrlWithQueryParams = useCallback((baseUrl: string, queryParams: Record<string, string>): string => {
    const urlObj = new URL(baseUrl);
    for (const [key, value] of Object.entries(queryParams)) {
      if (value.trim()) {
        urlObj.searchParams.set(key, value);
      }
    }
    return urlObj.toString();
  }, []);
  const handleAtomicImport = useCallback(async () => {
    if (!url || !probeResult || !selectedWalletAddress) return;

    // Clear any previous client errors
    setClientError(null);

    // Parse request body first (before changing step)
    let body: Record<string, unknown> | undefined;
    if (requestBody.trim()) {
      try {
        body = JSON.parse(requestBody);
      } catch {
        setClientError("Invalid JSON in request body. Please check your input.");
        return;
      }
    }
    setStep("importing");
    try {
      // Build final URL with query parameters
      const finalUrl = Object.keys(queryParamValues).length > 0 ? buildUrlWithQueryParams(url, queryParamValues) : url;

      // Call the atomic import endpoint with selected wallet
      const result_0 = await atomicImport({
        resourceUrl: finalUrl,
        method: requestMethod || probeResult.method || undefined,
        body,
        walletAddress: selectedWalletAddress
      });

      // Store result for success display
      setImportedEndpoint(result_0.endpoint);
      setPaymentDetails(result_0.paymentDetails);
      setStep("success");
    } catch {
      // Error handled by hook, go back to probe step
      setStep("probe");
    }
  }, [url, probeResult, requestBody, requestMethod, selectedWalletAddress, queryParamValues, buildUrlWithQueryParams, atomicImport]);
  const handleDone = useCallback(() => {
    if (importedEndpoint) {
      onImported?.(importedEndpoint);
    }
  }, [importedEndpoint, onImported]);
  const handleReset = useCallback(() => {
    setStep("input");
    setUrl("");
    setRequestBody("");
    setRequestMethod("");
    setShowDiscoveredParams(false);
    setShowResponseData(false);
    setSelectedWalletAddress("");
    setQueryParamValues({});
    setImportedEndpoint(null);
    setPaymentDetails(null);
    setClientError(null);
    resetProbe();
    resetImport();
    onImportAnother?.();
  }, [resetProbe, resetImport, onImportAnother]);

  // Check if all required query params are filled
  const hasRequiredQueryParams = useCallback(() => {
    if (!probeResult?.queryParams) return true;
    for (const [name_0, param_0] of Object.entries(probeResult.queryParams)) {
      if (param_0.required && !queryParamValues[name_0]?.trim()) {
        return false;
      }
    }
    return true;
  }, [probeResult?.queryParams, queryParamValues]);
  const isModal = variant === "modal";
  const renderInputStep = () => <div className="space-y-4">
      <div>
        <Input type="url" placeholder="https://api.example.com/x402-endpoint" value={url} onChange={e => setUrl(e.target.value)} className="w-full" />
        <p className="label-xs mt-2">Enter the URL of an x402-enabled API endpoint</p>
      </div>

      {isChecking && <div className="body-sm flex items-center gap-2">
          <PixelLoader size="sm" />
          Checking if endpoint exists...
        </div>}

      {exists && existingEndpoint && <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-900/20">
          <div className="flex items-start gap-2">
            <IconAlertInfoOutline18 className="mt-0.5 h-4 w-4 text-blue-600 dark:text-blue-500" />
            <div>
              <p className="text-sm font-medium text-blue-800 dark:text-blue-400">Endpoint already exists</p>
              <p className="mt-0.5 text-xs text-blue-700 dark:text-blue-500">
                "{existingEndpoint.name}" is already in the registry. You can continue to update it with a new test
                payment.
              </p>
            </div>
          </div>
        </div>}

      {probeError && <div className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/20">
          <div className="flex items-start gap-2">
            <IconAlertInfoOutline18 className="mt-0.5 h-4 w-4 text-red-600 dark:text-red-500" />
            <div>
              <p className="text-sm font-medium text-red-800 dark:text-red-400">Failed to probe endpoint</p>
              <p className="mt-0.5 text-xs text-red-700 dark:text-red-500">{probeError.message}</p>
            </div>
          </div>
        </div>}

      {/* Advanced options for endpoints that require input before returning 402 */}
      <Accordion type="single" collapsible className="border-ds-border-default border-t">
        <AccordionItem value="advanced" className="border-b-0">
          <AccordionTrigger className="text-ds-text-muted hover:text-ds-text-secondary py-3 text-sm hover:no-underline dark:hover:text-zinc-300">
            Advanced Options
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4">
              <div>
                <label className="text-ds-text-secondary mb-1.5 block text-sm font-medium">
                  HTTP Method (optional)
                </label>
                <Select value={requestMethod || "__auto__"} onValueChange={v => setRequestMethod(v === "__auto__" ? "" : v)}>
                  <SelectTrigger className="border-ds-border-default text-ds-text-primary bg-ds-surface-card h-10 w-full rounded-lg border text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__auto__">Auto-detect (POST, then GET)</SelectItem>
                    <SelectItem value="GET">GET</SelectItem>
                    <SelectItem value="POST">POST</SelectItem>
                    <SelectItem value="PUT">PUT</SelectItem>
                    <SelectItem value="PATCH">PATCH</SelectItem>
                    <SelectItem value="DELETE">DELETE</SelectItem>
                  </SelectContent>
                </Select>
                <p className="label-xs mt-1">
                  Leave empty to auto-detect. Specify if the endpoint uses a specific HTTP method.
                </p>
              </div>

              <div>
                <label className="text-ds-text-secondary mb-1.5 block text-sm font-medium">
                  Request Body (optional)
                </label>
                <Textarea placeholder='{"token": "BTC"}' value={requestBody} onChange={e_0 => setRequestBody(e_0.target.value)} className="h-20 w-full rounded-lg font-mono focus:border-blue-500 focus:ring-1 focus:ring-blue-500" />
                <p className="label-xs mt-1">
                  Some endpoints require valid input fields before returning payment requirements.
                </p>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>;
  const renderProbeStep = () => <div className="space-y-4">
      {probeError ? <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
          <div className="flex items-start gap-2">
            <IconAlertInfoOutline18 className="mt-0.5 h-4 w-4 text-red-600 dark:text-red-500" />
            <div>
              <p className="text-sm font-medium text-red-800 dark:text-red-400">Failed to probe endpoint</p>
              <p className="mt-0.5 text-xs text-red-700 dark:text-red-500">{probeError.message}</p>
            </div>
          </div>
        </div> : probeResult ? <>
          <div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-900/20">
            <div className="flex items-start gap-2">
              <IconCheckOutline18 className="mt-0.5 h-4 w-4 text-green-600 dark:text-green-500" />
              <div>
                <p className="text-sm font-medium text-green-800 dark:text-green-400">x402 endpoint detected</p>
                <p className="mt-0.5 text-xs text-green-700 dark:text-green-500">Payment requirements found</p>
              </div>
            </div>
          </div>

          <div className="border-ds-border-default dark:bg-ds-neutral-800/50 space-y-3 rounded-lg border bg-zinc-50 p-4">
            <h4 className="body-sm-medium">Payment Requirements</h4>

            {probeResult.accepts?.map((accept, i) => <div key={i} className="text-ds-text-subtle dark:text-ds-text-disabled flex flex-wrap gap-2 text-xs">
                <span className="inline-flex items-center rounded bg-blue-100 px-2 py-1 font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                  {formatX402Price(accept.maxAmountRequired, probeResult.tokenDecimals || 6)} {resolvedTokenSymbol}
                </span>
                <span className="bg-ds-neutral-200 dark:bg-ds-neutral-700 inline-flex items-center rounded px-2 py-1">
                  Network: {accept.network}
                </span>
                <span className="bg-ds-neutral-200 dark:bg-ds-neutral-700 inline-flex items-center rounded px-2 py-1">
                  Method: {probeResult.method || "POST"}
                </span>
              </div>)}

            <div className="label-xs flex items-center gap-1">
              <IconArrowUpRightOutline18 className="h-3 w-3" />
              Pay to: {probeResult.payTo}
            </div>
          </div>

          {/* Query Parameters Input Section */}
          {probeResult.queryParams && Object.keys(probeResult.queryParams).length > 0 && <div className="space-y-3 rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20">
              <h4 className="text-sm font-medium text-blue-900 dark:text-blue-300">Query Parameters</h4>
              <div className="space-y-3">
                {Object.entries(probeResult.queryParams).map(([name_1, param_1]) => <div key={name_1}>
                    <label className="mb-1 flex items-center gap-2 text-xs">
                      <code className="rounded bg-blue-100 px-1.5 py-0.5 font-mono text-blue-800 dark:bg-blue-900/40 dark:text-blue-400">
                        {name_1}
                      </code>
                      {param_1.required && <span className="rounded bg-red-100 px-1.5 py-0.5 text-red-700 dark:bg-red-900/40 dark:text-red-400">
                          required
                        </span>}
                      {param_1.description && <span className="text-ds-text-muted">— {param_1.description}</span>}
                    </label>
                    {param_1.enum && param_1.enum.length > 0 ? <Select value={queryParamValues[name_1] || "__empty__"} onValueChange={v_0 => setQueryParamValues(prev => ({
              ...prev,
              [name_1]: v_0 === "__empty__" ? "" : v_0
            }))}>
                        <SelectTrigger className="text-ds-text-primary bg-ds-surface-card h-10 w-full rounded-lg border border-blue-200 text-sm dark:border-blue-700">
                          <SelectValue placeholder={`Select ${name_1}...`} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__empty__">Select {name_1}...</SelectItem>
                          {param_1.enum.map(opt => <SelectItem key={String(opt)} value={String(opt)}>
                              {String(opt)}
                            </SelectItem>)}
                        </SelectContent>
                      </Select> : <Input type="text" placeholder={param_1.default !== undefined ? String(param_1.default) : `Enter ${name_1}`} value={queryParamValues[name_1] || ""} onChange={e_1 => setQueryParamValues(prev_0 => ({
              ...prev_0,
              [name_1]: e_1.target.value
            }))} className="border-blue-200 focus:border-blue-500 dark:border-blue-700" />}
                  </div>)}
              </div>
            </div>}

          {/* Discovered Parameters Section (collapsed reference) */}
          {(probeResult.queryParams || probeResult.bodyParams || probeResult.headerParams) && <div className="space-y-2">
              <button type="button" onClick={() => setShowDiscoveredParams(!showDiscoveredParams)} className="text-ds-text-secondary hover:text-ds-text-primary flex w-full items-center gap-1 text-sm font-medium dark:hover:text-white">
                {showDiscoveredParams ? <IconChevronDownOutline18 className="h-4 w-4" /> : <IconChevronRightOutline18 className="h-4 w-4" />}
                Parameter Reference
                <span className="bg-ds-neutral-200 text-ds-text-subtle dark:bg-ds-neutral-700 dark:text-ds-text-disabled ml-1 rounded px-1.5 py-0.5 text-xs">
                  {Object.keys(probeResult.queryParams || {}).length + Object.keys(probeResult.bodyParams || {}).length + Object.keys(probeResult.headerParams || {}).length}
                </span>
              </button>

              {showDiscoveredParams && <div className="space-y-2">
                  <ParamList title="Query Parameters" params={probeResult.queryParams} color="blue" />
                  <ParamList title="Body Parameters" params={probeResult.bodyParams} color="green" />
                  <ParamList title="Header Parameters" params={probeResult.headerParams} color="purple" />
                </div>}
            </div>}

          {/* Request body input */}
          <div>
            <label className="text-ds-text-secondary mb-1.5 block text-sm font-medium">
              Request Body (optional JSON)
            </label>
            <Textarea placeholder='{"key": "value"}' value={requestBody} onChange={e_2 => setRequestBody(e_2.target.value)} className="h-24 w-full rounded-lg font-mono focus:border-blue-500 focus:ring-1 focus:ring-blue-500" />
            <p className="label-xs mt-1">
              Optional data to send with the request. Will be used for testing and stored as example.
            </p>
          </div>

          {/* Wallet Selector */}
          <div className="border-ds-border-default dark:bg-ds-neutral-800/50 space-y-3 rounded-lg border bg-zinc-50 p-4">
            <div className="flex items-center gap-2">
              <IconCryptoWallet className="text-ds-text-muted h-4 w-4" />
              <h4 className="body-sm-medium">Payment Wallet</h4>
            </div>

            {isLoadingWallets ? <div className="body-sm flex items-center gap-2">
                <PixelLoader size="sm" />
                Loading wallets...
              </div> : orgWallets.length === 0 ? <div className="space-y-2">
                <p className="label-xs">No wallets found. Please create a wallet in Wallet Management first.</p>
                {!isModal && <Link href="/wallets">
                    <Button variant="white" size="sm">
                      <span className="flex items-center gap-2 text-xs">
                        <IconCryptoWallet className="h-3 w-3" />
                        Go to Wallets
                      </span>
                    </Button>
                  </Link>}
              </div> : <div className="space-y-2">
                <Select value={selectedWalletAddress} onValueChange={setSelectedWalletAddress}>
                  <SelectTrigger className="border-ds-border-default text-ds-text-primary bg-ds-surface-card h-10 w-full rounded-lg border text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {orgWallets.map(wallet => <SelectItem key={wallet.address} value={wallet.address}>
                        {wallet.name || "Wallet"}
                        {wallet.isDefault ? " (Default)" : ""} ({wallet.address.slice(0, 6)}...
                        {wallet.address.slice(-4)})
                      </SelectItem>)}
                  </SelectContent>
                </Select>
                {/* Show selected wallet address with copy button */}
                {selectedWalletAddress && <div className="bg-ds-neutral-100 dark:bg-ds-neutral-800 flex items-center gap-2 rounded-lg px-3 py-2">
                    <code className="text-ds-text-subtle dark:text-ds-text-disabled flex-1 truncate text-xs">
                      {selectedWalletAddress}
                    </code>
                    <button type="button" onClick={() => {
              navigator.clipboard.writeText(selectedWalletAddress);
            }} className="text-ds-text-muted hover:bg-ds-neutral-200 hover:text-ds-text-secondary dark:hover:bg-ds-neutral-700 flex-shrink-0 rounded p-1 transition-colors dark:hover:text-zinc-300" title="Copy address">
                      <IconCopyOutline18 className="h-3.5 w-3.5" />
                    </button>
                  </div>}
                {/* Show token balance */}
                {selectedWalletAddress && probeResult?.accepts?.[0] && <div className="mt-2">
                    {isLoadingBalance ? <div className="label-xs flex items-center gap-2">
                        <PixelLoader size="xs" />
                        Checking balance...
                      </div> : balanceCheckResult.formattedBalance !== null ? <div className="space-y-1">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-ds-text-muted">Available Balance:</span>
                          <span className={balanceCheckResult.isSufficient ? "font-medium text-green-600 dark:text-green-400" : "font-medium text-red-600 dark:text-red-400"}>
                            {balanceCheckResult.formattedBalance} {resolvedTokenSymbol}
                          </span>
                        </div>
                        {!balanceCheckResult.isSufficient && <div className="flex items-start gap-1.5 rounded-md bg-red-50 p-2 dark:bg-red-900/20">
                            <IconAlertInfoOutline18 className="mt-0.5 h-3 w-3 flex-shrink-0 text-red-500" />
                            <p className="text-xs text-red-700 dark:text-red-400">
                              Insufficient balance. You need at least{" "}
                              {formatX402Price(probeResult.accepts[0].maxAmountRequired, probeResult.tokenDecimals || 6)}{" "}
                              {resolvedTokenSymbol} to test this endpoint.
                            </p>
                          </div>}
                      </div> : null}
                  </div>}
              </div>}
          </div>

          {/* Import info */}
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-900/20">
            <div className="flex items-start gap-2">
              <IconSparkle3Outline18 className="mt-0.5 h-4 w-4 text-blue-600 dark:text-blue-500" />
              <div>
                <p className="text-sm font-medium text-blue-800 dark:text-blue-400">Ready to import</p>
                <p className="mt-0.5 text-xs text-blue-700 dark:text-blue-500">
                  Click "Pay & Import" to make a test payment and import this endpoint. AI will generate metadata from
                  the response automatically.
                </p>
              </div>
            </div>
          </div>

          {/* Error display (client-side or import error) */}
          {(clientError || importError) && <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
              <div className="flex items-start gap-2">
                <IconAlertInfoOutline18 className="mt-0.5 h-4 w-4 text-red-600 dark:text-red-500" />
                <div>
                  <p className="text-sm font-medium text-red-800 dark:text-red-400">
                    {clientError ? "Validation Error" : "Failed to import endpoint"}
                  </p>
                  <p className="mt-0.5 text-xs text-red-700 dark:text-red-500">{clientError || importError?.message}</p>
                </div>
              </div>
            </div>}
        </> : null}
    </div>;
  const renderImportingStep = () => <div className={`flex flex-col items-center justify-center ${isModal ? "py-8" : "py-16"}`}>
      <PixelLoader size={isModal ? "xl" : "2xl"} color="blue" />
      <p className={`text-ds-text-secondary mt-4 font-medium dark:text-zinc-300 ${isModal ? "text-sm" : "text-lg"}`}>
        Importing endpoint...
      </p>
      <p className={`text-ds-text-muted mt-1 ${isModal ? "text-xs" : "text-sm"}`}>
        Making test payment, generating AI metadata, and saving endpoint
      </p>
    </div>;
  const renderSuccessStep = () => <div className="space-y-4">
      <div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-900/20">
        <div className="flex items-start gap-2">
          <IconCheckOutline18 className={`text-green-600 dark:text-green-500 ${isModal ? "mt-0.5 h-4 w-4" : "mt-0.5 h-5 w-5"}`} />
          <div>
            <p className={`font-medium text-green-800 dark:text-green-400 ${isModal ? "text-sm" : "text-base"}`}>
              Endpoint imported successfully!
            </p>
            <p className={`mt-0.5 text-green-700 dark:text-green-500 ${isModal ? "text-xs" : "text-sm"}`}>
              The endpoint is now available in your workflow actions.
            </p>
          </div>
        </div>
      </div>

      {importedEndpoint && <div className="border-ds-border-default dark:bg-ds-neutral-800/50 space-y-3 rounded-lg border bg-zinc-50 p-4">
          <div className="flex items-center gap-2">
            <IconSparkle3Outline18 className={`text-blue-500 ${isModal ? "h-4 w-4" : "h-5 w-5"}`} />
            <span className={`text-ds-text-primary font-medium dark:text-white ${isModal ? "text-sm" : "text-base"}`}>
              {importedEndpoint.name}
            </span>
          </div>
          {importedEndpoint.description && <p className={`text-ds-text-subtle dark:text-ds-text-disabled ${isModal ? "text-xs" : "text-sm"}`}>
              {importedEndpoint.description}
            </p>}
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center rounded bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
              {importedEndpoint.category || "Uncategorized"}
            </span>
            {(importedEndpoint.tags || []).slice(0, 5).map(tag => <span key={tag} className="bg-ds-neutral-200 text-ds-text-secondary dark:bg-ds-neutral-700 inline-flex items-center rounded px-2 py-0.5 text-xs">
                {tag}
              </span>)}
          </div>
          <div className={`text-ds-text-muted flex items-center gap-2 ${isModal ? "text-xs" : "text-sm"}`}>
            <IconEarthOutline18 className={isModal ? "h-3 w-3" : "h-4 w-4"} />
            <span className="truncate">{importedEndpoint.baseUrl}</span>
          </div>
        </div>}

      {paymentDetails && <div className="border-ds-border-default bg-ds-surface-card space-y-2 rounded-lg border p-4">
          <h4 className="body-sm-medium">Payment Details</h4>
          <div className={`grid grid-cols-2 gap-2 ${isModal ? "text-xs" : "text-sm"}`}>
            <div>
              <span className="text-ds-text-muted">Network:</span>
              <span className="text-ds-text-secondary ml-1">{paymentDetails.network}</span>
            </div>
            <div>
              <span className="text-ds-text-muted">Amount:</span>
              <span className="text-ds-text-secondary ml-1">
                {formatX402Price(paymentDetails.amountPaid, importedEndpoint?.tokenDecimals || 6)}{" "}
                {importedEndpoint?.tokenSymbol || resolvedTokenSymbol}
              </span>
            </div>
            <div className="col-span-2">
              <span className="text-ds-text-muted">Transaction:</span>
              {paymentDetails.transaction ? <a href={`https://basescan.org/tx/${paymentDetails.transaction}`} target="_blank" rel="noopener noreferrer" className="ml-1 text-blue-600 hover:underline dark:text-blue-400">
                  {paymentDetails.transaction.slice(0, 10)}...{paymentDetails.transaction.slice(-8)}
                  <IconArrowUpRightOutline18 className="ml-1 inline h-3 w-3" />
                </a> : <span className="text-ds-text-disabled ml-1">Not available from server</span>}
            </div>
          </div>
        </div>}

      {/* Response Data Section */}
      {importedEndpoint?.exampleOutput !== undefined && importedEndpoint?.exampleOutput !== null && <div className="border-ds-border-default bg-ds-surface-card rounded-lg border">
          <button type="button" onClick={() => setShowResponseData(!showResponseData)} className="flex w-full items-center justify-between p-3 text-left">
            <div className="flex items-center gap-2">
              <IconCodeOutline18 className="text-ds-text-muted h-4 w-4" />
              <span className="body-sm-medium">Response Data</span>
            </div>
            {showResponseData ? <IconChevronDownOutline18 className="text-ds-text-muted h-4 w-4" /> : <IconChevronRightOutline18 className="text-ds-text-muted h-4 w-4" />}
          </button>
          {showResponseData && <div className="border-ds-border-default border-t p-3">
              <pre className={`bg-ds-neutral-100 dark:bg-ds-neutral-900 overflow-auto rounded p-3 text-xs text-zinc-800 dark:text-zinc-200 ${isModal ? "max-h-48" : "max-h-64"}`}>
                {JSON.stringify(importedEndpoint.exampleOutput, null, 2)}
              </pre>
            </div>}
        </div>}
    </div>;
  const renderStepContent = () => {
    switch (step) {
      case "input":
        return renderInputStep();
      case "probe":
        return renderProbeStep();
      case "importing":
        return renderImportingStep();
      case "success":
        return renderSuccessStep();
      default:
        return null;
    }
  };
  const getStepTitle = () => {
    switch (step) {
      case "input":
        return isModal ? "Import x402 Endpoint" : "Enter Endpoint URL";
      case "probe":
        return "Payment Details";
      case "importing":
        return "Importing...";
      case "success":
        return "Import Complete";
      default:
        return "Import x402 Endpoint";
    }
  };
  return {
    step,
    stepTitle: getStepTitle(),
    content: renderStepContent(),
    // Actions for footer
    canProbe: !!url && !isProbing,
    isProbing,
    handleProbe,
    canImport: !!probeResult && !probeError && !isImporting && !!selectedWalletAddress && hasRequiredQueryParams(),
    isImporting,
    handleAtomicImport,
    handleBack: () => setStep("input"),
    handleDone,
    handleReset,
    handleCancel: onCancel,
    // For step indicator
    importedEndpoint
  };
}